/**
 * Shared "not-found / processing" card for AI lookup modules
 * ===========================================================
 * Used by MCA21 / GST / OpenSanctions / eCourts when the API returns
 * `found=false`. Displays the AI advisory message, alternative steps,
 * direct portal URLs, and CDD / recommended-action pills.
 *
 * When `enableStrategy` is true (MCA21 only) and the user clicks "Suggest
 * Verification Strategy", calls /api/mca21/verification-strategy and
 * renders the GSTIN-first plan inline below.
 */
import React, { useState } from "react";
import axios from "axios";
import { AlertTriangle, Loader2, Sparkles, Calendar, ExternalLink, ListChecks, FileCheck2, Wand2, Hash, Receipt, Upload, ArrowRight, CheckCircle2 } from "lucide-react";

export default function NotFoundCard({ result, enableStrategy = false }) {
  const isNotFound = result.data_status === "NOT_FOUND";
  const accent = isNotFound ? "#dc2626" : "#f59e0b";

  const directUrls = [
    result.mca_direct_search_url,
    result.cin_direct_url,
    result.gst_direct_search_url,
    result.opensanctions_direct_url,
    result.ecourts_direct_url,
  ].filter(Boolean);

  const [strategy, setStrategy] = useState(null);
  const [loadingStrategy, setLoadingStrategy] = useState(false);

  const fetchStrategy = async () => {
    setLoadingStrategy(true);
    try {
      const name = result.company_name || result.legal_name || result.query_name || result.search_name;
      const { data } = await axios.post(
        `${window.location.origin}/api/mca21/verification-strategy`,
        { company_name: name },
        { withCredentials: true },
      );
      setStrategy(data.strategy);
    } catch (e) {
      console.error("strategy fetch failed", e);
    } finally { setLoadingStrategy(false); }
  };

  return (
    <div
      data-testid="not-found-card"
      style={{
        background: "#0d1117",
        border: `1px solid ${accent}`,
        borderRadius: 12,
        padding: 24,
        marginTop: 20,
      }}
    >
      <div className="flex items-center gap-3 mb-3">
        {isNotFound
          ? <AlertTriangle style={{ width: 22, height: 22, color: accent }} />
          : <Loader2 style={{ width: 22, height: 22, color: accent }} />}
        <div>
          <div style={{ color: "#f1f5f9", fontSize: 16, fontWeight: 700 }}>
            {isNotFound ? "No Record Found" : "Indexing in Progress"}
          </div>
          <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 2 }}>
            Searched: <code style={{ color: "#cbd5e1", fontFamily: "ui-monospace, monospace" }}>
              {result.query_name || result.company_name || result.legal_name || result.search_name || "—"}
            </code>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          {result.cdd_tier && (
            <span style={{ background: `${accent}22`, color: accent, border: `1px solid ${accent}55`, fontSize: 10, padding: "3px 8px", borderRadius: 4, fontWeight: 700, letterSpacing: 0.3, textTransform: "uppercase" }}>
              CDD: {result.cdd_tier}
            </span>
          )}
          {result.recommended_action && (
            <span style={{ background: "rgba(245,158,11,0.15)", color: "#f59e0b", border: "1px solid #f59e0b55", fontSize: 10, padding: "3px 8px", borderRadius: 4, fontWeight: 700, letterSpacing: 0.3, textTransform: "uppercase" }}>
              {result.recommended_action.replace(/_/g, " ")}
            </span>
          )}
        </div>
      </div>

      {result.message && (
        <div style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: 6, padding: 12, color: "#cbd5e1", fontSize: 13, lineHeight: 1.6, marginBottom: 14 }}>
          {result.message}
        </div>
      )}

      {result.ai_assessment && (
        <div style={{ background: "rgba(37,99,235,0.06)", border: "1px solid #2563eb55", borderRadius: 6, padding: 12, marginBottom: 14 }}>
          <div style={{ color: "#3b82f6", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
            <Sparkles size={11} /> AI Compliance Assessment
          </div>
          <div style={{ color: "#cbd5e1", fontSize: 12, lineHeight: 1.6 }}>{result.ai_assessment}</div>
        </div>
      )}

      {result.alternative_steps?.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
            Alternative Verification Steps
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, color: "#cbd5e1", fontSize: 12, lineHeight: 1.8 }}>
            {result.alternative_steps.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        {directUrls.map((u, i) => (
          <a key={i} href={u} target="_blank" rel="noopener noreferrer" style={{ background: "transparent", border: "1px solid #2563eb", color: "#2563eb", padding: "6px 12px", borderRadius: 6, fontSize: 11, fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}>
            <ExternalLink size={11} /> Verify directly
          </a>
        ))}
        {enableStrategy && !strategy && (
          <button
            data-testid="suggest-strategy-btn"
            onClick={fetchStrategy}
            disabled={loadingStrategy}
            style={{ background: "linear-gradient(135deg, #2563eb, #1d4ed8)", border: "none", color: "white", padding: "6px 14px", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: loadingStrategy ? "wait" : "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            {loadingStrategy ? <Loader2 className="animate-spin" size={11} /> : <Wand2 size={11} />}
            {loadingStrategy ? "Generating plan..." : "Suggest Verification Strategy"}
          </button>
        )}
      </div>

      {result.estimated_availability && (
        <div style={{ marginTop: 14, color: "#f59e0b", fontSize: 11, display: "flex", alignItems: "center", gap: 6 }}>
          <Calendar size={11} /> Estimated availability: {result.estimated_availability}
        </div>
      )}

      {enableStrategy && <ProgressiveFallback searchedName={result.company_name || result.query_name || result.legal_name || result.search_name} />}

      {strategy && <StrategyBlock strategy={strategy} />}
    </div>
  );
}


// =====================================================================
// =======  Progressive Fallback (CIN → GSTIN → COI upload)  ===========
// =====================================================================
function ProgressiveFallback({ searchedName }) {
  const [mode, setMode] = useState(null);     // null | "cin" | "gstin" | "coi"
  const [val, setVal] = useState("");
  const [loading, setLoading] = useState(false);
  const [found, setFound] = useState(null);   // result object on success
  const [error, setError] = useState(null);

  const CIN_RX = /^[UL][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/i;
  const GSTIN_RX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9][A-Z][0-9A-Z]$/i;

  const submit = async () => {
    const v = (val || "").trim().toUpperCase();
    setError(null); setFound(null);

    if (mode === "cin") {
      if (!CIN_RX.test(v)) { setError("Invalid CIN format. Expected 21 characters like U74999MH2026PTC123456"); return; }
      setLoading(true);
      try {
        const { data } = await axios.post(`${window.location.origin}/api/mca21/lookup`, { cin: v }, { withCredentials: true });
        setFound({ kind: "cin", data: data.result });
      } catch (e) { setError(e.response?.data?.detail || "Lookup failed"); }
      finally { setLoading(false); }
    } else if (mode === "gstin") {
      if (!GSTIN_RX.test(v)) { setError("Invalid GSTIN format. Expected 15 characters like 27AABCS1234F1Z5"); return; }
      setLoading(true);
      try {
        const { data } = await axios.post(`${window.location.origin}/api/gst/lookup`, { gstin: v }, { withCredentials: true });
        setFound({ kind: "gstin", data: data.result });
      } catch (e) { setError(e.response?.data?.detail || "Lookup failed"); }
      finally { setLoading(false); }
    }
  };

  return (
    <div style={{ marginTop: 18, background: "#080c12", border: "1px solid #1e2530", borderRadius: 10, padding: 16 }} data-testid="progressive-fallback">
      <div className="flex items-center gap-2 mb-3">
        <ArrowRight size={14} style={{ color: "#3b82f6" }} />
        <strong style={{ color: "#f1f5f9", fontSize: 13 }}>Recently incorporated? Provide an identifier to verify directly:</strong>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: mode ? 14 : 0 }}>
        <FallbackOption icon={Hash}    title="Provide CIN"   subtitle="Direct MCA21 fast-path" active={mode === "cin"}   onClick={() => { setMode("cin"); setVal(""); setFound(null); setError(null); }} testId="fallback-cin" />
        <FallbackOption icon={Receipt} title="Provide GSTIN" subtitle="Cross-verify via GSTN"   active={mode === "gstin"} onClick={() => { setMode("gstin"); setVal(""); setFound(null); setError(null); }} testId="fallback-gstin" />
        <FallbackOption icon={Upload}  title="Upload COI"    subtitle="Request from applicant" active={mode === "coi"}   onClick={() => { setMode("coi"); setVal(""); setFound(null); setError(null); }} testId="fallback-coi" />
      </div>

      {mode === "cin" && (
        <div style={{ display: "flex", gap: 8 }}>
          <input data-testid="fallback-cin-input" value={val} onChange={(e) => setVal(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="e.g. U74999MH2026PTC123456" maxLength={21} style={{ flex: 1, background: "#0d1117", border: "1px solid #1e2530", borderRadius: 6, padding: "8px 12px", color: "#f1f5f9", fontSize: 12, fontFamily: "ui-monospace, monospace", letterSpacing: 1 }} />
          <button onClick={submit} disabled={loading} data-testid="fallback-cin-submit" style={{ background: "linear-gradient(135deg, #2563eb, #1d4ed8)", border: "none", color: "white", padding: "8px 16px", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: loading ? "wait" : "pointer", display: "flex", alignItems: "center", gap: 6 }}>
            {loading ? <Loader2 className="animate-spin" size={12} /> : <ArrowRight size={12} />} Fast Lookup
          </button>
        </div>
      )}

      {mode === "gstin" && (
        <div style={{ display: "flex", gap: 8 }}>
          <input data-testid="fallback-gstin-input" value={val} onChange={(e) => setVal(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="e.g. 27AABCS1234F1Z5" maxLength={15} style={{ flex: 1, background: "#0d1117", border: "1px solid #1e2530", borderRadius: 6, padding: "8px 12px", color: "#f1f5f9", fontSize: 12, fontFamily: "ui-monospace, monospace", letterSpacing: 1 }} />
          <button onClick={submit} disabled={loading} data-testid="fallback-gstin-submit" style={{ background: "linear-gradient(135deg, #10b981, #047857)", border: "none", color: "white", padding: "8px 16px", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: loading ? "wait" : "pointer", display: "flex", alignItems: "center", gap: 6 }}>
            {loading ? <Loader2 className="animate-spin" size={12} /> : <ArrowRight size={12} />} Verify via GSTN
          </button>
        </div>
      )}

      {mode === "coi" && (
        <div style={{ background: "rgba(245,158,11,0.06)", border: "1px solid #f59e0b55", borderRadius: 8, padding: 14 }} data-testid="fallback-coi-block">
          <div style={{ color: "#f59e0b", fontSize: 12, fontWeight: 700, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
            <FileCheck2 size={13} /> Request Certificate of Incorporation
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, color: "#cbd5e1", fontSize: 12, lineHeight: 1.8 }}>
            <li>Ask <strong>{searchedName || "the applicant"}</strong> to email a clear scan of their <strong>Certificate of Incorporation (COI)</strong>.</li>
            <li>Confirm the <strong>CIN</strong> printed on the COI and re-run the CIN fast-path above.</li>
            <li>If still unclear, request the <strong>PAN of company</strong> and <strong>Directors list with DIN numbers</strong>.</li>
            <li>Do not onboard until at least one verified identifier (CIN, GSTIN, or COI) is on file.</li>
          </ul>
          <a href={`mailto:?subject=Document%20Request%20—%20Certificate%20of%20Incorporation&body=Hello%2C%20As%20part%20of%20our%20KYC%20process%20at%20Rudrik%2C%20please%20share%20a%20scanned%20copy%20of%20your%20Certificate%20of%20Incorporation%20(COI)%20along%20with%20the%20CIN%20and%20GSTIN%20for%20your%20entity%20%22${encodeURIComponent(searchedName || "")}%22.%20Thanks.`} style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 10, background: "transparent", border: "1px solid #f59e0b", color: "#f59e0b", padding: "6px 12px", borderRadius: 6, fontSize: 11, fontWeight: 700, textDecoration: "none" }}>
            <Upload size={11} /> Open Email Draft
          </a>
        </div>
      )}

      {error && <div style={{ marginTop: 10, color: "#ef4444", fontSize: 11 }}>{error}</div>}

      {found && (
        <div style={{ marginTop: 14, background: "rgba(34,197,94,0.06)", border: "1px solid #22c55e55", borderRadius: 8, padding: 14 }} data-testid="fallback-found-block">
          <div style={{ color: "#22c55e", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
            <CheckCircle2 size={12} /> Verified via {found.kind === "cin" ? "MCA21 CIN Direct" : "GSTN Portal"}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
            {found.kind === "cin" ? (
              <>
                <KV k="Company"      v={found.data.company_name} />
                <KV k="CIN"          v={found.data.cin} mono />
                <KV k="Status"       v={found.data.status} />
                <KV k="Incorporated" v={found.data.incorporation_date} />
                <KV k="Risk"         v={found.data.risk_level} valueColor={found.data.risk_level === "LOW" ? "#22c55e" : found.data.risk_level === "MEDIUM" ? "#f59e0b" : "#ef4444"} />
              </>
            ) : (
              <>
                <KV k="Legal Name"   v={found.data.legal_name} />
                <KV k="GSTIN"        v={found.data.gstin} mono />
                <KV k="Status"       v={found.data.gstin_status} />
                <KV k="Registered"   v={found.data.registration_date} />
                <KV k="Risk"         v={found.data.risk_level} valueColor={found.data.risk_level === "LOW" ? "#22c55e" : found.data.risk_level === "MEDIUM" ? "#f59e0b" : "#ef4444"} />
              </>
            )}
          </div>
          {found.data.ai_assessment && <div style={{ marginTop: 10, color: "#cbd5e1", fontSize: 11, lineHeight: 1.5, background: "#0d1117", border: "1px solid #1e2530", borderRadius: 6, padding: 10 }}>{found.data.ai_assessment}</div>}
        </div>
      )}
    </div>
  );
}


function FallbackOption({ icon: Icon, title, subtitle, active, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} style={{
      background: active ? "rgba(37,99,235,0.10)" : "#0d1117",
      border: `1px solid ${active ? "#2563eb" : "#1e2530"}`,
      borderRadius: 8, padding: "10px 12px", textAlign: "left", cursor: "pointer",
      display: "flex", alignItems: "center", gap: 10,
      transition: "border-color 0.15s",
    }}>
      <Icon size={16} style={{ color: active ? "#3b82f6" : "#64748b", flexShrink: 0 }} />
      <div style={{ flex: 1, overflow: "hidden" }}>
        <div style={{ color: active ? "#f1f5f9" : "#cbd5e1", fontSize: 12, fontWeight: 700 }}>{title}</div>
        <div style={{ color: "#64748b", fontSize: 10 }}>{subtitle}</div>
      </div>
    </button>
  );
}

function KV({ k, v, mono, valueColor }) {
  return (
    <div>
      <div style={{ color: "#64748b", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 3 }}>{k}</div>
      <div style={{ color: valueColor || "#f1f5f9", fontSize: 12, fontWeight: 600, fontFamily: mono ? "ui-monospace, monospace" : "inherit", wordBreak: "break-word" }}>{v || "—"}</div>
    </div>
  );
}


function StrategyBlock({ strategy }) {
  const canOnboard = !!strategy.can_onboard_now;
  return (
    <div data-testid="verification-strategy-block" style={{ marginTop: 18, background: "#080c12", border: "1px solid #2563eb55", borderRadius: 10, padding: 18 }}>
      <div className="flex items-center gap-2 mb-2">
        <Wand2 size={14} style={{ color: "#3b82f6" }} />
        <strong style={{ color: "#f1f5f9", fontSize: 13, letterSpacing: 0.3 }}>Verification Strategy — {strategy.strategy || "GSTIN_FIRST"}</strong>
        <span style={{ marginLeft: "auto", background: canOnboard ? "rgba(34,197,94,0.18)" : "rgba(239,68,68,0.18)", color: canOnboard ? "#22c55e" : "#ef4444", border: `1px solid ${canOnboard ? "#22c55e" : "#ef4444"}55`, fontSize: 10, padding: "3px 8px", borderRadius: 4, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
          {canOnboard ? "Can Onboard Now" : "Hold Onboarding"}
        </span>
        {strategy.cdd_tier_until_mca_available && (
          <span style={{ background: "rgba(168,85,247,0.15)", color: "#a855f7", border: "1px solid #a855f755", fontSize: 10, padding: "3px 8px", borderRadius: 4, fontWeight: 700 }}>
            CDD: {strategy.cdd_tier_until_mca_available}
          </span>
        )}
      </div>
      {strategy.reason && <div style={{ color: "#cbd5e1", fontSize: 12, lineHeight: 1.6, marginBottom: 14 }}>{strategy.reason}</div>}

      {/* Steps timeline */}
      {strategy.steps?.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
            <ListChecks size={11} /> Sequenced Verification Steps
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {strategy.steps.map((s, i) => (
              <div key={i} style={{ display: "flex", gap: 12, padding: 12, background: "#0d1117", border: "1px solid #1e2530", borderRadius: 8 }}>
                <div style={{ flexShrink: 0, width: 28, height: 28, borderRadius: "50%", background: "rgba(37,99,235,0.18)", color: "#3b82f6", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 12 }}>{s.step ?? i + 1}</div>
                <div style={{ flex: 1 }}>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <strong style={{ color: "#f1f5f9", fontSize: 12 }}>{s.action}</strong>
                    <span style={{ color: "#94a3b8", fontSize: 11 }}>· {s.instruction}</span>
                    {s.cost && <span style={{ marginLeft: "auto", background: "rgba(34,197,94,0.12)", color: "#22c55e", border: "1px solid #22c55e55", fontSize: 9, padding: "1px 6px", borderRadius: 3, fontWeight: 700 }}>{s.cost}</span>}
                  </div>
                  {s.why && <div style={{ color: "#94a3b8", fontSize: 11, marginBottom: 4 }}>⤷ {s.why}</div>}
                  {s.endpoint && <code style={{ color: "#cbd5e1", fontFamily: "ui-monospace, monospace", fontSize: 10, background: "#080c12", padding: "2px 6px", borderRadius: 3, display: "inline-block", marginTop: 2, wordBreak: "break-all" }}>{s.endpoint}</code>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {strategy.documents_to_collect_now?.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
            <FileCheck2 size={11} /> Documents to Collect Now
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 6 }}>
            {strategy.documents_to_collect_now.map((doc, i) => (
              <div key={i} style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 6, padding: "8px 12px", color: "#cbd5e1", fontSize: 11, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e", flexShrink: 0 }} /> {doc}
              </div>
            ))}
          </div>
        </div>
      )}

      {strategy.onboard_conditions?.length > 0 && (
        <div style={{ background: canOnboard ? "rgba(34,197,94,0.06)" : "rgba(239,68,68,0.06)", border: `1px solid ${canOnboard ? "#22c55e55" : "#ef444455"}`, borderRadius: 6, padding: 12 }}>
          <div style={{ color: canOnboard ? "#22c55e" : "#ef4444", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
            Onboarding Conditions ({canOnboard ? "ALL must be met" : "blocked"})
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, color: "#cbd5e1", fontSize: 11, lineHeight: 1.8 }}>
            {strategy.onboard_conditions.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
